import React, { useState } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  User,
  CurrencyDollar,
  CreditCard,
  Coins,
  ToggleLeft,
  ToggleRight,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { Partner } from "../types";
import { formatCurrency } from "../utils/calculations";
import { useModal } from "../hooks/useModal";

const Collaboration: React.FC = () => {
  const { state, dispatch } = useData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [partnerFormData, setPartnerFormData] = useState({
    name: "",
    age: 0,
    relationship: "spouse" as Partner["relationship"],
    monthlyIncome: 0,
    monthlyExpenses: 0,
    liquidAssets: 0,
    isActive: false,
  });

  // Add useModal hook for the partner modal
  const modalRef = useModal({
    isOpen: showAddModal,
    onClose: () => {
      setShowAddModal(false);
      setEditingPartner(null);
      resetForm();
    },
  });

  const currentUserPartners = state.partners.filter(
    (p) => p.userId === state.currentUserId
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const partnerData: Partner = {
      id: editingPartner?.id || `partner-${Date.now()}`,
      userId: state.currentUserId!,
      name: partnerFormData.name,
      age: partnerFormData.age || undefined,
      relationship: partnerFormData.relationship,
      monthlyIncome: partnerFormData.monthlyIncome,
      monthlyExpenses: partnerFormData.monthlyExpenses,
      liquidAssets: partnerFormData.liquidAssets,
      assets: [],
      debts: [],
      incomeSources: [],
      transactions: [],
      isActive: partnerFormData.isActive,
      createdAt: editingPartner?.createdAt || new Date().toISOString(),
    };

    if (editingPartner) {
      dispatch({ type: "UPDATE_PARTNER", payload: partnerData });
    } else {
      dispatch({ type: "ADD_PARTNER", payload: partnerData });
    }

    setShowAddModal(false);
    setEditingPartner(null);
    resetForm();
  };

  const handleEdit = (partner: Partner) => {
    setEditingPartner(partner);
    setPartnerFormData({
      name: partner.name,
      age: partner.age || 0,
      relationship: partner.relationship,
      monthlyIncome: partner.monthlyIncome,
      monthlyExpenses: partner.monthlyExpenses,
      liquidAssets: partner.liquidAssets || 0,
      isActive: partner.isActive,
    });
    setShowAddModal(true);
  };

  const handleDelete = (partnerId: string) => {
    if (window.confirm("Are you sure you want to delete this partner?")) {
      dispatch({ type: "DELETE_PARTNER", payload: partnerId });
    }
  };

  const handleToggleActive = (partner: Partner) => {
    dispatch({
      type: "UPDATE_PARTNER",
      payload: { ...partner, isActive: !partner.isActive },
    });
  };

  const resetForm = () => {
    setPartnerFormData({
      name: "",
      age: 0,
      relationship: "spouse",
      monthlyIncome: 0,
      monthlyExpenses: 0,
      liquidAssets: 0,
      isActive: false,
    });
  };

  const handleDollarInputChange = (field: string, value: string) => {
    const cleanValue = value.replace(/[^0-9.]/g, "");
    const numValue = parseFloat(cleanValue) || 0;
    setPartnerFormData({
      ...partnerFormData,
      [field]: numValue,
    });
  };

  const formatDollarValue = (value: number): string => {
    if (value === 0) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getRelationshipIcon = (relationship: Partner["relationship"]) => {
    switch (relationship) {
      case "spouse":
        return <User className="w-5 h-5 text-pink-500" weight="duotone" />;
      case "partner":
        return <User className="w-5 h-5 text-purple-500" weight="duotone" />;
      case "family-member":
        return <User className="w-5 h-5 text-blue-500" weight="duotone" />;
      case "other":
        return <User className="w-5 h-5 text-gray-500" weight="duotone" />;
      default:
        return <User className="w-5 h-5 text-gray-500" weight="duotone" />;
    }
  };

  const getRelationshipLabel = (relationship: Partner["relationship"]) => {
    switch (relationship) {
      case "spouse":
        return "Spouse";
      case "partner":
        return "Partner";
      case "family-member":
        return "Family Member";
      case "other":
        return "Other";
      default:
        return "Other";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div></div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2">
          <Plus className="w-4 h-4" weight="duotone" />
          <span>Add Partner</span>
        </button>
      </div>

      {/* Partners List */}
      {currentUserPartners.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentUserPartners.map((partner) => (
            <div
              key={partner.id}
              className="card hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                    {getRelationshipIcon(partner.relationship)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {partner.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {getRelationshipLabel(partner.relationship)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleActive(partner)}
                    className={`p-1 rounded transition-colors ${
                      partner.isActive
                        ? "text-green-600 hover:text-green-700"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                    title={
                      partner.isActive
                        ? "Disable partner in calculations"
                        : "Include partner in calculations"
                    }>
                    {partner.isActive ? (
                      <ToggleRight className="w-4 h-4" weight="duotone" />
                    ) : (
                      <ToggleLeft className="w-4 h-4" weight="duotone" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(partner)}
                    className="btn-icon"
                    title="Edit partner">
                    <PencilSimple className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(partner.id)}
                    className="btn-icon"
                    title="Delete partner">
                    <Trash className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Monthly Income:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(partner.monthlyIncome)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Monthly Expenses:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(partner.monthlyExpenses)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Liquid Assets:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(partner.liquidAssets || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Net Monthly:
                  </span>
                  <span
                    className={`font-medium ${
                      partner.monthlyIncome - partner.monthlyExpenses >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}>
                    {formatCurrency(
                      partner.monthlyIncome - partner.monthlyExpenses
                    )}
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Included in calculations:
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      partner.isActive ? "text-green-600" : "text-gray-500"
                    }`}>
                    {partner.isActive ? "Yes" : "No"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Partners Added
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Add your first partner to start collaborating on financial planning.
          </p>
          <button onClick={() => setShowAddModal(true)} className="btn-primary">
            Add Partner
          </button>
        </div>
      )}

      {/* Add/Edit Partner Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            ref={modalRef}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editingPartner ? "Edit Partner" : "Add Partner"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={partnerFormData.name}
                  onChange={(e) =>
                    setPartnerFormData({
                      ...partnerFormData,
                      name: e.target.value,
                    })
                  }
                  className="input w-full"
                  placeholder="Partner's name"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Age
                  </label>
                  <input
                    type="number"
                    value={partnerFormData.age || ""}
                    onChange={(e) =>
                      setPartnerFormData({
                        ...partnerFormData,
                        age: parseInt(e.target.value) || 0,
                      })
                    }
                    className="input w-full"
                    placeholder="Age"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Relationship
                  </label>
                  <select
                    value={partnerFormData.relationship}
                    onChange={(e) =>
                      setPartnerFormData({
                        ...partnerFormData,
                        relationship: e.target.value as Partner["relationship"],
                      })
                    }
                    className="select w-full">
                    <option value="spouse">Spouse</option>
                    <option value="partner">Partner</option>
                    <option value="family-member">Family Member</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Monthly Income
                </label>
                <input
                  type="text"
                  value={formatDollarValue(partnerFormData.monthlyIncome)}
                  onChange={(e) =>
                    handleDollarInputChange("monthlyIncome", e.target.value)
                  }
                  className="input w-full"
                  placeholder="$0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Monthly Expenses
                </label>
                <input
                  type="text"
                  value={formatDollarValue(partnerFormData.monthlyExpenses)}
                  onChange={(e) =>
                    handleDollarInputChange("monthlyExpenses", e.target.value)
                  }
                  className="input w-full"
                  placeholder="$0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Liquid Assets
                </label>
                <input
                  type="text"
                  value={formatDollarValue(partnerFormData.liquidAssets)}
                  onChange={(e) =>
                    handleDollarInputChange("liquidAssets", e.target.value)
                  }
                  className="input w-full"
                  placeholder="$0"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={partnerFormData.isActive}
                  onChange={(e) =>
                    setPartnerFormData({
                      ...partnerFormData,
                      isActive: e.target.checked,
                    })
                  }
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label
                  htmlFor="isActive"
                  className="text-sm text-gray-700 dark:text-gray-300">
                  Include partner data in calculations
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingPartner(null);
                    resetForm();
                  }}
                  className="btn-secondary flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  {editingPartner ? "Update Partner" : "Add Partner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collaboration;
